import React, { Fragment, h } from "preact";
import { AppPage } from "../_common/components/app-page";
import { Card, CF, R } from "../_common/layout";
import { t } from "../_i18n";
import { useHistory } from "react-router";
import { connect } from "react-redux";
import { useEffect, useState, useMemo, useRef } from "preact/hooks";
import { Form as FinalForm } from "react-final-form";
import {
  Select,
  UploadFile,
  FormFieldSplittedDate,
} from "../_common/forms/components/components";
import ButtonSpinner from "../_common/components/buttons/buttonSpinner/buttonSpinner";
import Badge from "../_common/components/Badge";
import Spinner from "../_common/components/Spinner";
import {
  getCertApplicationStatus,
  getAceCertApplicationStatus,
  uploadCertPaymentReceipt,
  uploadAceCertPaymentReceipt,
  generateVoucher,
} from "../_rx/certificate-application/actions";
import { Link } from "react-router-dom";

const STATUS_PENDING_APPLICATION = 1;
const STATUS_VALIDATED = 2;
const STATUS_UPLOADED_GITU = 3;
const STATUS_VALIDATED_INSPECTION = 4;
const STATUS_REJECTED = 5;

const statusBadgeTextDic = {
  [STATUS_PENDING_APPLICATION]: t("Pendiente validación"),
  [STATUS_UPLOADED_GITU]: t("Validado"),
  [STATUS_VALIDATED_INSPECTION]: t("Validado"),
  [STATUS_VALIDATED]: t("Validado"),
  [STATUS_REJECTED]: t("Rechazado"),
};

const statusBadgeClassDic = {
  [STATUS_PENDING_APPLICATION]: "badge-warning",
  [STATUS_UPLOADED_GITU]: "badge-success translucent",
  [STATUS_VALIDATED_INSPECTION]: "badge-success translucent",
  [STATUS_VALIDATED]: "badge-success translucent",
  [STATUS_REJECTED]: "badge-danger",
};

const bonosOptionsList = [
  {
    name: "Gratis",
    key: "free",
  },
  {
    name: "Bono 99€",
    key: "general_voucher",
  },
  {
    name: "Bono nuevo alumno 99€",
    key: "new_student_voucher",
  },
];

const BadgeComponent = ({ status, extraClass }) => (
  <Badge
    className={`t-upper width-fit-content badge font-small ${extraClass || ""} ${statusBadgeClassDic[status] || "d-none"}`}
  >
    {t(statusBadgeTextDic[status])}
  </Badge>
);

function CertificateApplication({
  studentId,
  finishedAcademicPrograms,
  getCertApplicationStatus,
  getAceCertApplicationStatus,
  certificateApplicationStatus,
  aceCertificateApplicationStatus,
  uploadCertPaymentReceipt,
  uploadAceCertPaymentReceipt,
  generateVoucher,
}) {
  /** Variables **/
  const formRef = useRef(null);
  const academicPrograms = finishedAcademicPrograms
    ?.filter(
      (ap) =>
        (ap.formative_zone === "MAD" || ap.formative_zone === "CAT") && ap.closed_academic_record,
    )
    .map((ap) => ({
      name: ap.title + " " + `(${ap.formative_zone_code})`,
      key: ap.id,
    }));
  const showMultipleCicles = academicPrograms.length > 1;

  /** States **/
  const [file, setFile] = useState(null);
  const [aceFile, setAceFile] = useState(null);
  const [fileURL, setFileURL] = useState("#");
  const [aceFileURL, setAceFileURL] = useState("#");
  const [loading, setLoading] = useState(false);
  const [academicProgramId, setAcademicProgramId] = useState(academicPrograms[0]?.key);
  const [bonoId, setBonoId] = useState(bonosOptionsList[0].key);
  const [isLoading, setIsLoading] = useState(true);
  const [bonoEnviado, setBonoEnviado] = useState(false);
  const [aportaEmpresa, setAaportaEmpresa] = useState(false);
  const [bonoGenerado, setBonoGenerado] = useState(false);
  const [pagado, setPagado] = useState(null);
  const [cartUrl, setCartUrl] = useState(null);

  /** Memos **/
  const selectedAP = useMemo(
    () => finishedAcademicPrograms.find((ap) => ap.id === academicProgramId),
    [academicProgramId, finishedAcademicPrograms],
  );
  const isMad = selectedAP?.formative_zone === "MAD";
  const isCat = selectedAP?.formative_zone === "CAT";

  /** Lifecycles **/
  useEffect(() => {
    const fetchData = async () => {
      await getCertApplicationStatus(studentId, academicProgramId);
      await getAceCertApplicationStatus(studentId, academicProgramId);
      setIsLoading(false);
    };

    fetchData();
  }, [studentId]);

  useEffect(() => {
    setAaportaEmpresa(selectedAP?.company_provided);
    setBonoGenerado(selectedAP?.voucher_generated);
    setPagado(selectedAP?.paid_at);
    setCartUrl(selectedAP?.cart_url);
  }, [selectedAP]);

  /** Methods **/
  const shouldShowForm = (status) => {
    if (!status?.documents) return true;
    switch (status?.status_id) {
      case STATUS_REJECTED:
      case undefined:
        return true;
      default:
        return false;
    }
  };

  const shouldShowRequestCard = (status) => {
    if (!status?.documents) return false;
    return true;
  };

  const formatDateDDMMYYYY = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const replaceCertDate = (html, date) => {
    if (!html || !date) return html;
    return html.replace("dd-mm-aaaa", formatDateDDMMYYYY(date));
  };

  const handleUploadFile = (event, type) => {
    let eventFile = event.target.files[0];
    if (type === "ace") {
      setAceFile(eventFile);
      setAceFileURL(URL.createObjectURL(eventFile));
    } else {
      setFile(eventFile);
      setFileURL(URL.createObjectURL(eventFile));
    }
  };

  const handleAPSelect = async (e) => {
    const academicProgramId = +e.target.value;

    setAcademicProgramId(academicProgramId);

    formRef.current?.reset();
    handleRemoveFiles();

    setIsLoading(true);
    await getCertApplicationStatus(studentId, academicProgramId);
    await getAceCertApplicationStatus(studentId, academicProgramId);
    setIsLoading(false);
  };

  const handleRemoveFiles = () => {
    setFile(null);
    setFileURL("#");
    setAceFile(null);
    setAceFileURL("#");
  };

  const handleSubmitFile = async (data) => {
    setLoading(true);
    if (file) {
      setLoading(true);
      await uploadCertPaymentReceipt(
        studentId,
        file,
        academicProgramId,
        data.payed_at,
      );
      setLoading(false);
    }
    if (aceFile) {
      setLoading(true);
      await uploadAceCertPaymentReceipt(studentId, {
        file: aceFile,
        academicProgramId,
      });
      setLoading(false);
    }
    setLoading(true);
    await getCertApplicationStatus(studentId, academicProgramId);
    await getAceCertApplicationStatus(studentId, academicProgramId);
    setLoading(false);
    handleRemoveFiles();
    setLoading(false);
  };

  const getCertificateApplicationHtml = () => {
    const statusId =
      certificateApplicationStatus?.documents?.length &&
      certificateApplicationStatus?.status_id;
    const aceStatusId =
      aceCertificateApplicationStatus?.documents?.length &&
      aceCertificateApplicationStatus?.status_id;
    const isPending = statusId === STATUS_PENDING_APPLICATION;
    const isRejected = statusId === STATUS_REJECTED;
    const aceIsRejected = aceStatusId === STATUS_REJECTED;
    const isValidated =
      statusId === STATUS_VALIDATED ||
      statusId === STATUS_UPLOADED_GITU ||
      statusId === STATUS_VALIDATED_INSPECTION;

    if (isRejected || aceIsRejected) {
      const comments = [
        certificateApplicationStatus?.comments,
        aceCertificateApplicationStatus?.comments,
      ].filter(Boolean);

      return (
        <Fragment>
          <p>Hemos detectado los siguientes errores en la documentación que nos has aportado:</p><br />
          <div class="p-4 bg-red-200">
            <ul>{comments.map(comment => <li>- {comment}</li>)}</ul>
          </div><br />
          <p>Por favor, vuelve a subir la documentación.</p>
        </Fragment>
      )
    }

    if (isPending) return (
      <Fragment>
        <p>Hemos recibido correctamente el <span class="font-bold">justificante de pago de las tasas del título</span> que nos has enviado.
        </p><br /><p>En los próximos días, <span class="font-bold">Secretaría Académica revisará y validará la documentación</span>. Una vez finalizada la validación, te informaremos del estado del trámite y de los siguientes pasos, si procede.</p><br />
        <p>Mientras tanto, no es necesario que realices ninguna otra acción.</p></Fragment>
    );

    if (isValidated) {
      const html = (
        <Fragment>
          <p>La solicitud de tramitación de título se inició con éxito.</p>
          <br />

          <p>
            <span className="font-bold">
              Tu título llegará a nuestra sede en un plazo aproximado de entre dos a cuatro años.
            </span>{" "}
            Ten en cuenta que este plazo es orientativo y{" "}
            <span className="font-bold">no depende de iLERNA.</span>
          </p>
          <br />

          {!pagado ?
            ((bonoGenerado || !aportaEmpresa) ?
              <Fragment>
                <p>
                  Clica <a className="font-bold underline" href={cartUrl} target="_blank">aquí</a> para realizar el pago de las tasas para continuar con la solicitud del título.
                </p>
                <br />
              </Fragment>
              : null)
            :
            <Fragment>
              <p>
                Podrás descargar un
                <Link to="/expediente">
                  <span className="font-bold underline mx-1">certificado oficial de finalización</span>
                </Link>
                de tu ciclo formativo, a la espera de recibir tu título oficial.
              </p>
              <br />
            </Fragment>
          }

          <p>
            Estos certificados te permitirán hacer trámites, solicitudes y gestiones oficiales,
            mientras desde Secretaría Académica de iLERNA gestionamos tu titulación.
          </p>
          <br />
        </Fragment>
      );
      return replaceCertDate(html);
    }

    if (isCat) {
      return (<Fragment><p>¡Enhorabuena por finalizar tus estudios! Ya puedes realizar el pago de las tasas para tu título oficial al Departamento de Educación de Cataluña. Una vez realizado, sube el comprobante de pago en este apartado y, tras su validación, solicitaremos tu título. Ten en cuenta que el proceso de expedición del Ministerio de Educación puede tardar hasta 4 años.</p><br /><p>Para realizar el trámite correctamente, consulta nuestra guía paso a paso: <u><a class="font-bold" href="https://storage.googleapis.com/ilerna-core/infografias/cat_expedicion_titulo.pdf" target="_blank">guía de pago de tasas</a></u> en el Departamento de Educación de Cataluña.</p><br /><p>Si has tenido una reducción o exención de tasas en el pago (familias numerosas u otros casos especiales), adjunta también la documentación que lo acredite para que podamos verificarlo.</p></Fragment>)
    }

    if (isMad) {
      return (<Fragment><p>¡Enhorabuena por finalizar tus estudios! Ya puedes realizar el pago de las tasas para tu título oficial a la Comunidad de Madrid. Una vez realizado, sube el comprobante de pago en este apartado y, tras su validación, solicitaremos tu título. Ten en cuenta que el proceso de expedición del Ministerio de Educación puede tardar hasta 4 años.</p><br /><p>Para realizar el trámite correctamente, consulta nuestra guía paso a paso: <u><a class="font-bold" href="https://storage.googleapis.com/ilerna-core/infografias/mad_expedicion_titulo.pdf.pdf?usp=sharing" target="_blank">guía de pago de tasas</a></u> en la Comunidad de Madrid.</p><br /><p>Si has tenido una reducción o exención de tasas en el pago (familias numerosas u otros casos especiales), adjunta también la documentación que lo acredite para que podamos verificarlo.</p></Fragment>)
    }

    return null;
  };

  const isCertificateValidated = (status) => {
    if (!status?.documents) return false;
    switch (status?.status_id) {
      case STATUS_VALIDATED:
      case STATUS_UPLOADED_GITU:
      case STATUS_VALIDATED_INSPECTION:
        return true;
      default:
        return false;
    }
  };

  const handleBonoSubmit = async () => {
    setLoading(true);
    const resp = await generateVoucher(
      selectedAP?.formative_zone,
      studentId,
      {
        academicProgramId: academicProgramId,
        voucherType: bonoId
      })

    if (!resp.error) {
      setBonoEnviado(true);
      window.location.reload();
    }

    setLoading(false);
  };

  const renderRequestCard = (request) => {
    return (
      <Card smOnly={false}>
        <p className="mb-1 font-bold">{request.academic_program_name}</p>
        <BadgeComponent status={request?.status_id} />
      </Card>
    );
  };

  const renderSpinner = () => (
    <div className="flex justify-center my-6">
      <div className="relative h-6">
        <Spinner className="w-6 h-6" />
      </div>
      <p className="pl-10 pt-0 text-center">Cargando datos ...</p>
    </div>
  );

  /** Render **/
  return (
    <CF className="max-width-980 m-x2-top">
      {showMultipleCicles && (
        <div className="mb-4">
          <div className="font-bold">{t("Selecciona el ciclo")}:</div>
          <Select
            noEmptyOption
            options={academicPrograms}
            value={academicProgramId}
            onChange={handleAPSelect}
            required
          />
        </div>
      )}
      <FinalForm
        onSubmit={handleSubmitFile}
        initialValues={{ payed_at: null }}
        validate={(values) => {
          const errors = {};
          if (!values.payed_at) {
            errors.payed_at = t("Campo obligatorio");
          }
          return errors;
        }}
        render={({ handleSubmit, form }) => {
          formRef.current = form;
          const { invalid, submitting } = form.getState();
          return (
            <Fragment>
              {isLoading ? (
                renderSpinner()
              ) : (
                getCertificateApplicationHtml()
              )}
              {!isLoading && (
                <Fragment>
                  {aportaEmpresa && !pagado && !bonoGenerado && !bonoEnviado &&
                    isCertificateValidated(certificateApplicationStatus) && (
                      <Fragment>
                        <div className="mb-2 mt-4 font-bold">
                          {t("Selecciona el tipo de bono")}:
                        </div>
                        <div className="flex flex-col gap-4 lg:flex-row">
                          <div className="w-full lg:w-9/12">
                            <Select
                              noEmptyOption
                              options={bonosOptionsList}
                              value={bonoId}
                              onChange={(event) =>
                                setBonoId(event.target.value)
                              }
                              required
                            />
                          </div>
                          <div className="w-full lg:w-3/12">
                            <ButtonSpinner
                              showSpinner={loading}
                              disabled={submitting}
                              classNameContainer="w-full lg:w-auto"
                              className="w-full h-[48px] break-keep whitespace-nowrap lg:w-auto"
                              onClick={() => handleBonoSubmit()}
                            >
                              {t("Seleccionar bono")}
                            </ButtonSpinner>
                          </div>
                        </div>
                      </Fragment>
                    )}
                  <div className="my-4 flex gap-4">
                    {!isCertificateValidated(certificateApplicationStatus) && (
                      <div className="w-full bg-white p-4">
                        <h2 className="font-bold">
                          {t("Comprobante del pago de tasas en educación")}
                        </h2>
                        {shouldShowForm(certificateApplicationStatus) ? (
                          <div>
                            {!file ? (
                              <div className="pt-6">
                                <UploadFile
                                  classNameButton="btn btn-ghost"
                                  accept="application/pdf,.docx,.doc,.odt"
                                  onChange={handleUploadFile}
                                >
                                  {t("Subir archivo")}
                                </UploadFile>
                                <span className="font-color-879CA1">
                                  {t("transfer_expedient_doc_types")}
                                </span>
                              </div>
                            ) : (
                              <div className="m-bottom d-flex flex-column sm-no-flex-column mt-2">
                                <a
                                  className="m-r font-color-149BAB m-bottom sm-no-m-bottom"
                                  href={fileURL}
                                  download={file.name}
                                >
                                  {file.name}
                                </a>
                                <span
                                  className="font-color-soft-gray t-underline cursor-pointer"
                                  onClick={handleRemoveFiles}
                                >
                                  {t("Quitar archivo")}
                                </span>
                              </div>
                            )}
                            <form
                              id="payed-form"
                              onSubmit={handleSubmit}
                              noValidate
                            >
                              <h2 className="-mb-2 mt-4 font-bold">
                                {t("Fecha en la que se ha pagado la tasa:")}
                              </h2>
                              <R>
                                <div className="col-100 md-col-60">
                                  <FormFieldSplittedDate
                                    key={academicProgramId}
                                    name="payed_at"
                                  />
                                </div>
                              </R>
                              <button type="submit" hidden />
                            </form>
                          </div>
                        ) : null}
                        {shouldShowRequestCard(certificateApplicationStatus) && (
                          <Fragment>
                            <hr className="mb-3 mt-2" />
                            <h2 className="m-bottom font-bold">
                              {t("Estado de la petición")}
                            </h2>
                          </Fragment>
                        )}

                        {shouldShowRequestCard(certificateApplicationStatus) &&
                          renderRequestCard(certificateApplicationStatus)}
                      </div>
                    )}
                    {!isCertificateValidated(certificateApplicationStatus) && (
                      <div className="w-full bg-white p-4">
                        <div>
                          <h2 className="font-bold">
                            {t("Justificación que acredite la reducción o exención de tasas (opcional)")}
                          </h2>
                          {shouldShowForm(aceCertificateApplicationStatus) ? (
                            <div>
                              {!aceFile ? (
                                <div className="pt-6">
                                  <UploadFile
                                    classNameButton="btn btn-ghost"
                                    accept="application/pdf,.docx,.doc,.odt"
                                    onChange={(e) => handleUploadFile(e, "ace")}
                                  >
                                    {t("Subir archivo")}
                                  </UploadFile>
                                  <span className="font-color-879CA1">
                                    {t("transfer_expedient_doc_types")}
                                  </span>
                                </div>
                              ) : (
                                <div className="m-bottom d-flex flex-column sm-no-flex-column mt-2">
                                  <a
                                    className="m-r font-color-149BAB m-bottom sm-no-m-bottom"
                                    href={aceFileURL}
                                    download={aceFile.name}
                                  >
                                    {aceFile.name}
                                  </a>
                                  <span
                                    className="font-color-soft-gray t-underline cursor-pointer"
                                    onClick={handleRemoveFiles}
                                  >
                                    {t("Quitar archivo")}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                        {shouldShowRequestCard(aceCertificateApplicationStatus) && (
                          <Fragment>
                            <hr className="mb-3 mt-2" />
                            <h2 className="m-bottom font-bold">
                              {t("Estado de la petición")}
                            </h2>
                          </Fragment>
                        )}
                        {shouldShowRequestCard(aceCertificateApplicationStatus) &&
                          renderRequestCard(aceCertificateApplicationStatus)}
                      </div>
                    )}
                  </div>
                  {!isCertificateValidated(certificateApplicationStatus) &&
                    (shouldShowForm(certificateApplicationStatus) || shouldShowForm(aceCertificateApplicationStatus)) && (
                      <div className="flex w-full justify-end">
                        <ButtonSpinner
                          showSpinner={loading}
                          disabled={
                            !aceFile &&
                            (!file || !academicProgramId || invalid || submitting)
                          }
                          onClick={() =>
                            file
                              ? document
                                .getElementById("payed-form")
                                ?.requestSubmit()
                              : handleSubmitFile()
                          }
                        >
                          {t("Enviar solicitud")}
                        </ButtonSpinner>
                      </div>
                    )}
                </Fragment>
              )}
            </Fragment>
          );
        }}
      />
    </CF>
  );
}

CertificateApplication = connect(
  ({ auth, certificateApplication, status }) => {
    return {
      studentId: auth.loggedUser.id,
      certificateApplicationStatus: certificateApplication?.status,
      aceCertificateApplicationStatus: certificateApplication?.aceStatus,
      finishedAcademicPrograms: status.student_meta.finished_academic_programs,
    };
  },
  {
    getCertApplicationStatus,
    getAceCertApplicationStatus,
    uploadCertPaymentReceipt,
    uploadAceCertPaymentReceipt,
    generateVoucher,
  },
)(CertificateApplication);

export default () => {
  const history = useHistory();
  return (
    <AppPage
      name="certificate-application"
      title={t("Solicitud de título")}
      back={history.goBack}
      class="max-width-980"
    >
      <CertificateApplication />
    </AppPage>
  );
};
